import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { customerAuthApi } from '../lib/api';
import AuthShell from '../components/AuthShell';

/**
 * The token is validated on mount so an expired link says so immediately,
 * rather than after the user has typed a new password twice.
 */
export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [checking, setChecking] = useState(true);
  const [valid, setValid] = useState(false);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState<{ sessionsRevoked: number } | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) { setChecking(false); return; }
    customerAuthApi
      .validateResetToken(token)
      .then((r) => setValid(Boolean(r.data?.valid)))
      .catch(() => setValid(false))
      .finally(() => setChecking(false));
  }, [token]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) { setError(t('auth.resetMismatch')); return; }
    setLoading(true);
    try {
      const res = await customerAuthApi.resetPassword(token, password);
      setDone({ sessionsRevoked: res.data?.sessionsRevoked ?? 0 });
      setTimeout(() => navigate('/login'), 4000);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title={t('auth.resetTitle')}>

        {checking ? (
          <p className="text-sm text-gray-500 text-center">{t('common.loading')}</p>
        ) : done ? (
          <>
            <div className="p-3 bg-green-50 border border-green-200 text-green-800 rounded-lg text-sm mb-4">
              {t('auth.resetDone')}
              {done.sessionsRevoked > 0 && ` ${t('auth.resetSessions', { count: done.sessionsRevoked })}`}
            </div>
            <Link to="/login" className="block w-full text-center bg-brand-600 text-white py-3 rounded-lg font-medium hover:bg-brand-700">
              {t('auth.backToLogin')}
            </Link>
          </>
        ) : !valid ? (
          <>
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm mb-4">
              {t('auth.resetInvalid')}
            </div>
            <Link to="/forgot-password" className="block w-full text-center bg-brand-600 text-white py-3 rounded-lg font-medium hover:bg-brand-700">
              {t('auth.forgotSubmit')}
            </Link>
          </>
        ) : (
          <form onSubmit={submit} className="space-y-5">
            {error && (
              <div role="alert" className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                {error}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.newPassword')}</label>
              <input type="password" required minLength={8}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500"
                value={password} onChange={(e) => setPassword(e.target.value)} />
              <p className="text-xs text-gray-500 mt-1">{t('auth.passwordRule')}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.confirmPassword')}</label>
              <input type="password" required minLength={8}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500"
                value={confirm} onChange={(e) => setConfirm(e.target.value)} />
            </div>
            <button type="submit" disabled={loading || !password || !confirm}
              className="w-full bg-brand-600 text-white py-3 rounded-lg font-medium hover:bg-brand-700 disabled:opacity-50">
              {loading ? t('auth.pleaseWait') : t('auth.resetSubmit')}
            </button>
          </form>
        )}
    </AuthShell>
  );
}
